import { ref, computed } from 'vue'
import {
  completeReminderTask,
  continueReminderTask,
  convertReminderTaskToTodo,
  getMyReminderTaskPage,
  postponeReminderTask
} from '@/api/reminder'
import { setupReminderSoundUnlock } from '@/utils/reminderSound'
import { useGlobalToastCenter } from '@/composables/useGlobalToastCenter'

// 模块级单例状态（跨组件共享，模式与 src/composables/useMessageStore.js 一致）
const reminders = ref([])          // 完整待展示队列
const seenIds = new Set()          // 本次会话已入队的 id
const continuedSendKeys = new Map() // 延续提醒后，等下一次发送时间变化再重新展示
const dashboardList = ref([])      // 前 5 条非 CLOSED，供 Dashboard 订阅
const { pushToast, removeToastBySourceId } = useGlobalToastCenter()
let pollTimer = null
let pollGeneration = 0
const POLL_INTERVAL = 30000
const MAX_VISIBLE = 3
const MAX_QUEUE = 30

// 派生
const visibleReminders = computed(() => reminders.value.slice(0, MAX_VISIBLE))
const overflowCount = computed(() => Math.max(0, reminders.value.length - MAX_VISIBLE))

function toToastItem(r) {
  return {
    id: r.id,
    title: r.title,
    content: r.content,
    status: r.status,
    statusDesc: r.statusDesc,
    creatorId: r.creatorId,
    creatorName: r.creatorName,
    firstSendTime: r.firstSendTime,
    lastSendTime: r.lastSendTime
  }
}

function pushReminderToast(item, toastItem = toToastItem(item)) {
  pushToast({
    id: `reminder:${item.id}`,
    source: 'reminder',
    rawId: item.id,
    title: item.title || '未命名提醒',
    content: item.content || '暂无内容',
    meta: item.creatorName || item.statusDesc || '',
    time: item.lastSendTime || item.firstSendTime || '',
    sortTime: item.lastSendTime || item.firstSendTime || '',
    variant: 'reminder',
    actions: ['complete'],
    payload: toastItem
  })
}

function buildSendKey(item) {
  return item?.lastSendTime || item?.firstSendTime || ''
}

function startPolling() {
  stopPolling()
  const gen = ++pollGeneration
  pollTimer = setInterval(async () => {
    try {
      await fetchAndDiff(gen)
    } catch (e) {
      console.error('[useReminderToast] 轮询失败', e)
    }
  }, POLL_INTERVAL)
}

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
  pollGeneration++
}

function reset() {
  stopPolling()
  reminders.value = []
  dashboardList.value = []
  seenIds.clear()
  continuedSendKeys.clear()
}

async function init() {
  setupReminderSoundUnlock()
  startPolling()
  await fetchAndDiff(pollGeneration)
}

async function fetchAndDiff(gen) {
  try {
    const data = await getMyReminderTaskPage({
      bizType: 'CUSTOM_REMINDER',
      pageNum: 1,
      pageSize: MAX_QUEUE
    })
    if (gen !== pollGeneration) return

    const records = data?.records || []
    const nonClosed = records.filter(r => r.status !== 'CLOSED')
    const displayable = nonClosed.filter(item => {
      const continuedSendKey = continuedSendKeys.get(item.id)
      return !continuedSendKey || continuedSendKey !== buildSendKey(item)
    })
    dashboardList.value = displayable.slice(0, 5).map(toToastItem)

    // 对账：列表页等外部关闭的项，本队列内同步移除（避免 ghost card）
    const closedIds = new Set(records.filter(r => r.status === 'CLOSED').map(r => r.id))
    reminders.value = reminders.value.filter(r => !closedIds.has(r.id))
    closedIds.forEach(id => {
      seenIds.delete(id)
      continuedSendKeys.delete(id)
      removeToastBySourceId('reminder', id)
    })

    const newOnes = displayable.filter(r => !seenIds.has(r.id))
    newOnes.forEach(item => {
      seenIds.add(item.id)
      const toastItem = toToastItem(item)
      reminders.value.push(toastItem)
      pushReminderToast(item, toastItem)
    })
  } catch (e) {
    console.error('[useReminderToast] 加载提醒失败', e)
  }
}

async function loadDashboardList(gen = pollGeneration) {
  const data = await getMyReminderTaskPage({
    bizType: 'CUSTOM_REMINDER',
    pageNum: 1,
    pageSize: 5
  })
  if (gen !== pollGeneration) return
  const nonClosed = (data?.records || []).filter(r => r.status !== 'CLOSED')
  const displayable = nonClosed.filter(item => {
    const continuedSendKey = continuedSendKeys.get(item.id)
    return !continuedSendKey || continuedSendKey !== buildSendKey(item)
  })
  dashboardList.value = displayable.slice(0, 5).map(toToastItem)
}

async function closeReminder(id) {
  return processReminder(id, 'complete')
}

async function processReminder(id, action, sourceItem, options = {}) {
  const gen = pollGeneration
  if (action === 'continue') {
    await continueReminderTask(id)
  } else if (action === 'postpone') {
    await postponeReminderTask(id, { nextSendTime: options.nextSendTime })
  } else if (action === 'todo') {
    await convertReminderTaskToTodo(id, options.todo)
  } else {
    await completeReminderTask(id)
  }
  if (gen !== pollGeneration) return
  const currentItem = sourceItem
    || reminders.value.find(r => r.id === id)
    || dashboardList.value.find(r => r.id === id)
  if (action === 'continue' || action === 'postpone') {
    continuedSendKeys.set(id, buildSendKey(currentItem))
    seenIds.delete(id)
  } else {
    continuedSendKeys.delete(id)
  }
  reminders.value = reminders.value.filter(r => r.id !== id)
  dashboardList.value = dashboardList.value.filter(r => r.id !== id)
  removeToastBySourceId('reminder', id)
  if (action === 'continue' || action === 'postpone') {
    return
  }
  try {
    await loadDashboardList(gen)
  } catch (e) {
    console.error('[useReminderToast] 刷新提醒列表失败', e)
  }
}

function dismissReminder(id) {
  reminders.value = reminders.value.filter(r => r.id !== id)
  removeToastBySourceId('reminder', id)
}

// 外部入口关闭后同步移除：toast 队列 + Dashboard 卡片源（用于 /reminders/my-tasks 列表页的关闭操作）
// 仅做本地过滤，不调 loadDashboardList：本路径不引入额外 API 调用；下次 30s 轮询会做完整对账
// 不动 seenIds：防止"关闭→重新打开"场景被错误抑制
function syncClosedReminder(id) {
  reminders.value = reminders.value.filter(r => r.id !== id)
  dashboardList.value = dashboardList.value.filter(r => r.id !== id)
  seenIds.delete(id)
  continuedSendKeys.delete(id)
  removeToastBySourceId('reminder', id)
}

function isReminderSuppressed(item) {
  const continuedSendKey = continuedSendKeys.get(item?.id)
  return Boolean(continuedSendKey && continuedSendKey === buildSendKey(item))
}

export function useReminderToast() {
  return {
    reminders,
    visibleReminders,
    overflowCount,
    dashboardList,
    init,
    stopPolling,
    reset,
    closeReminder,
    processReminder,
    dismissReminder,
    syncClosedReminder,
    isReminderSuppressed
  }
}
